import {
  Calendar,
  ChevronRight,
  FileText,
  BarChart3,
  Award,
  TrendingUp,
  ChevronLeft
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLazyGetInterviewTestResultsQuery } from '../../services/startTestAPI/resultsTestApi';

const InterviewResultsTable = ({ userId }: { userId: string }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [pageData, setPageData] = useState({
    data: [
      {
        id: '',
        createdDate: '',
        testName: ''
      }
    ],
    pagesize: 10,
    totaltests: 0,
    currentpage: 0,
    totalpages: 0,
    message: 'Sucussfully fetched the test results',
    status: 'success'
  });
  const [getInterviewResultsData, { data, isSuccess }] =
    useLazyGetInterviewTestResultsQuery({});

  const handlePageChange = async (newPage: number) => {
    if (
      newPage >= 0 &&
      newPage < pageData.totalpages &&
      newPage !== currentPage
    ) {
      setLoading(true);
      setCurrentPage(newPage);
      const page = newPage;
      const size = 10;
      getInterviewResultsData({ size, page, userId });
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };
  const showFullTestDetails = (id: string) => {
    navigate(`/interviewFullResults/${id}/${userId}`);
  };
  useEffect(() => {
    if (isSuccess) {
      setPageData(data);
    }
  }, [data]);
  useEffect(() => {
    const page = 0;
    const size = 10;
    getInterviewResultsData({ size, page, userId });
  }, [userId]);

  return (
    <div className='min-h-screen rounded-xl bg-gradient-to-br from-purple-50 to-blue-50 p-1 md:p-6'>
      <div className='mx-auto max-w-4xl'>
        <div className='mb-6 rounded-xl bg-white p-2 shadow-lg md:p-6'>
          <div className='rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 p-2 text-white md:p-6'>
            <h1 className='mb-2 flex items-center gap-3 text-base font-bold md:text-2xl'>
              <Award size={28} />
              Test Results Dashboard
            </h1>
            <p className='text-purple-100'>
              View and analyze your assessment performance
            </p>
          </div>
        </div>

        <div className='mb-6 grid grid-cols-1 gap-1 md:grid-cols-3 md:gap-4'>
          <div className='rounded-xl bg-white p-2 shadow-lg md:p-6'>
            <div className='flex items-center gap-3'>
              <div className='rounded-lg bg-purple-100 p-3'>
                <FileText className='text-purple-600' size={24} />
              </div>
              <div>
                <h3 className='text-2xl font-bold text-gray-800'>
                  {pageData.totaltests}
                </h3>
                <p className='text-sm text-gray-600'>Total Tests</p>
              </div>
            </div>
          </div>

          <div className='rounded-xl bg-white p-2 shadow-lg md:p-6'>
            <div className='flex items-center gap-3'>
              <div className='rounded-lg bg-blue-100 p-3'>
                <TrendingUp className='text-blue-600' size={24} />
              </div>
              <div>
                <h3 className='text-2xl font-bold text-gray-800'>
                  {pageData.totalpages}
                </h3>
                <p className='text-sm text-gray-600'>Total Pages</p>
              </div>
            </div>
          </div>

          <div className='rounded-xl bg-white p-2 shadow-lg md:p-6'>
            <div className='flex items-center gap-3'>
              <div className='rounded-lg bg-green-100 p-3'>
                <BarChart3 className='text-green-600' size={24} />
              </div>
              <div>
                <h3 className='text-2xl font-bold text-gray-800'>
                  {pageData.currentpage + 1}
                </h3>
                <p className='text-sm text-gray-600'>Current Page</p>
              </div>
            </div>
          </div>
        </div>

        <div className='rounded-xl bg-white p-2 shadow-lg md:p-6'>
          <h2 className='mb-4 flex items-center gap-2 text-xl font-semibold text-gray-800'>
            <FileText size={24} className='text-purple-600' />
            Your Test Results
          </h2>

          <div className='space-y-3'>
            {loading ? (
              <div className='flex items-center justify-center py-12'>
                <div className='h-8 w-8 animate-spin rounded-full border-b-2 border-purple-600'></div>
                <span className='ml-3 text-gray-600'>Loading tests...</span>
              </div>
            ) : pageData.data.length > 0 ? (
              pageData.data.map(test => {
                const formattedDate = formatDate(test.createdDate);

                return (
                  <div
                    key={test.id}
                    onClick={() => showFullTestDetails(test.id)}
                    className='group flex cursor-pointer items-center justify-between rounded-lg border border-gray-200 p-1 transition-all hover:border-purple-300 hover:bg-purple-50 md:p-4'
                  >
                    <div className='flex items-center gap-4'>
                      <div className='rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 p-3 text-white'>
                        <FileText size={20} />
                      </div>
                      <div>
                        <h3 className='font-medium text-gray-800 transition-colors group-hover:text-purple-700'>
                          {test.testName}
                        </h3>
                        <div className='mt-1 flex items-center gap-4 text-sm text-gray-600'>
                          <div className='flex items-center gap-1'>
                            <Calendar size={14} />
                            {formattedDate.date}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='flex items-center text-purple-600 transition-colors group-hover:text-purple-800'>
                      <span className='mr-2 text-sm font-medium'>
                        View Results
                      </span>
                      <ChevronRight size={20} />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className='py-12 text-center text-gray-500'>
                <FileText size={48} className='mx-auto mb-4 text-gray-300' />
                <p>No tests found.</p>
              </div>
            )}
          </div>

          <div className='mt-6 border-t border-gray-200 pt-4'>
            <div className='flex flex-col items-center justify-between gap-2 md:flex-row md:gap-0'>
              <div className='text-sm text-gray-600'>
                <span>
                  Showing {pageData?.data.length} of {pageData?.totaltests}{' '}
                  tests
                </span>
                <span className='ml-4'>
                  Page {pageData?.currentpage + 1} of {pageData?.totalpages}
                </span>
              </div>

              <div className='flex items-center gap-2'>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0 || loading}
                  className={`flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    currentPage === 0 || loading
                      ? 'cursor-not-allowed text-gray-400'
                      : 'text-purple-600 hover:bg-purple-50 hover:text-purple-700'
                  }`}
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>

                <div className='flex items-center gap-1'>
                  {Array.from({ length: pageData.totalpages }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => handlePageChange(i)}
                      disabled={loading}
                      className={`h-10 w-10 rounded-lg text-sm font-medium transition-colors ${
                        i === currentPage
                          ? 'bg-purple-600 text-white'
                          : loading
                            ? 'cursor-not-allowed text-gray-400'
                            : 'text-gray-600 hover:bg-purple-50 hover:text-purple-700'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === pageData?.totalpages - 1 || loading}
                  className={`flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    currentPage === pageData?.totalpages - 1 || loading
                      ? 'cursor-not-allowed text-gray-400'
                      : 'text-purple-600 hover:bg-purple-50 hover:text-purple-700'
                  }`}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewResultsTable;
